import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { QueueEncounter, QueueFilters, QueueSortField, QueueViewMode, SortOrder } from '../types'

// v1 sub-filters + v2 chip values. Sub-filter inside the Query bucket.
export type QueryBucketFilter =
  | 'all'
  | 'system'
  | 'coder'
  | 'mine'
  | 'awaiting_dispatch'
  | 'awaiting_provider'
  | 'awaiting_client_admin'
  | 'responded'
  | 'expired'
  | 'blocking'

/**
 * Persisted queue UI state. We snapshot the filter/sort/page selection so
 * that when the coder backs out of an encounter detail page the queue
 * restores the same bucket they were working from. Session-scoped so a
 * fresh tab still starts on a clean queue.
 */
export interface QueueSnapshot {
  viewMode: QueueViewMode
  filters: QueueFilters
  sortField: QueueSortField
  sortOrder: SortOrder
  pageIndex: number
  pageSize: number
  queryBucketActive: boolean
  queryBucketFilter: QueryBucketFilter
  /** Server-issued opaque cursors for each visited page. Index 0 is undefined (page 1). */
  cursorStack: (string | undefined)[]
}

/**
 * Queue context — preserves the queryKey used to load the queue list the
 * coder was working from when they opened an encounter. Persisted to
 * sessionStorage (per-tab) so a hard refresh on a chart detail page still
 * resolves prev/next against the coder's actual bucket. Without this, the
 * react-query cache is empty after refresh AND `serverParams` is null, so
 * useEncounterNavigation falls through to the unfiltered global queue —
 * which includes completed/exported charts and makes the queue counter
 * jump between buckets.
 *
 * `serverParams` is the BE-ready params object (already CSV-joined etc.)
 * so the in-page queue drawer + prev/next nav can refetch the same bucket
 * without relying on the React-Query cache surviving refreshes.
 *
 * `wrapPageData` is intentionally NOT persisted — it's tied to a specific
 * in-flight wrap-to-last action, not the bucket identity.
 */
/**
 * Cached "wrap-to-last" page data. Set when the coder presses Prev from
 * the absolute first row of the bucket — the BE is hit once with a
 * REVERSED sort_order to fetch the last pageSize rows in a single
 * roundtrip, then JS-reverses them back to original order. The hook
 * prefers this slot over the react-query cache so the wrap target
 * resolves without chain-walking every page.
 */
export interface WrapPageData {
  encounters: QueueEncounter[]
  pageIndex: number
  total: number
}

/**
 * Origin list the coder navigated from. The encounter detail page (unified-
 * review) reads this to know which API to call for the compact list drawer +
 * prev/next nav so the walk stays inside the originating dataset.
 */
export type ContextSource = 'queue' | 'history-exported' | 'history-archived'

interface QueueContextState {
  queryKey: unknown[] | null
  bucketLabel: string | null
  filterChips: string[]
  serverParams: Record<string, string | number> | null
  /** Origin list (queue vs history-exported vs history-archived). */
  source: ContextSource
  /** Snapshot of the queue's filter/sort state so back-nav restores the bucket. */
  snapshot: QueueSnapshot | null
  /** See WrapPageData docstring. Cleared on any non-wrap navigation. */
  wrapPageData: WrapPageData | null
  setContext: (
    queryKey: unknown[],
    bucketLabel?: string | null,
    filterChips?: string[],
    serverParams?: Record<string, string | number> | null,
    source?: ContextSource,
  ) => void
  setSnapshot: (snapshot: QueueSnapshot) => void
  setWrapPageData: (data: WrapPageData | null) => void
  clear: () => void
}

export const useQueueContextStore = create<QueueContextState>()(
  persist(
    (set) => ({
      queryKey: null,
      bucketLabel: null,
      filterChips: [],
      serverParams: null,
      source: 'queue',
  snapshot: null,
      wrapPageData: null,
      setContext: (queryKey, bucketLabel = null, filterChips = [], serverParams = null, source) =>
        set((prev) => ({
      queryKey,
      bucketLabel,
      filterChips,
      serverParams,
      source: source ?? prev.source,
    })),
      setSnapshot: (snapshot) => set({ snapshot }),
      setWrapPageData: (data) => set({ wrapPageData: data }),
      clear: () => set({ queryKey: null, bucketLabel: null, filterChips: [], serverParams: null, source: 'queue', snapshot: null, wrapPageData: null }),
    }),
    {
      name: 'workspace-queue-context',
      storage: createJSONStorage(() => sessionStorage),
      // Persist only the bucket-identity fields. wrapPageData is in-flight nav
      // state; setters are reconstructed from the factory on every load.
      partialize: (state) => ({
        queryKey: state.queryKey,
        bucketLabel: state.bucketLabel,
        filterChips: state.filterChips,
        serverParams: state.serverParams,
        snapshot: state.snapshot,
      }),
    },
  ),
)

/**
 * Drawer toggle — session-scoped. Always closed on fresh page load.
 *
 * Encounter list and notes share the same right-side slot, so they're
 * mutually exclusive: opening one closes the other.
 */
interface DrawerState {
  open: boolean
  toggle: () => void
  setOpen: (open: boolean) => void
}

export const useEncounterDrawerStore = create<DrawerState>()((set, get) => ({
  open: false,
  toggle: () => {
    const next = !get().open
    if (next) {
      useNotesDrawerStore.getState().setOpen(false)
      useFlagsDrawerStore.getState().setOpen(false)
    }
    set({ open: next })
  },
  setOpen: (open) => {
    if (open) {
      useNotesDrawerStore.getState().setOpen(false)
      useFlagsDrawerStore.getState().setOpen(false)
    }
    set({ open })
  },
}))

export const useNotesDrawerStore = create<DrawerState>()((set, get) => ({
  open: false,
  toggle: () => {
    const next = !get().open
    if (next) {
      useEncounterDrawerStore.getState().setOpen(false)
      useFlagsDrawerStore.getState().setOpen(false)
    }
    set({ open: next })
  },
  setOpen: (open) => {
    if (open) {
      useEncounterDrawerStore.getState().setOpen(false)
      useFlagsDrawerStore.getState().setOpen(false)
    }
    set({ open })
  },
}))

export const useFlagsDrawerStore = create<DrawerState>()((set, get) => ({
  open: false,
  toggle: () => {
    const next = !get().open
    if (next) {
      useEncounterDrawerStore.getState().setOpen(false)
      useNotesDrawerStore.getState().setOpen(false)
    }
    set({ open: next })
  },
  setOpen: (open) => {
    if (open) {
      useEncounterDrawerStore.getState().setOpen(false)
      useNotesDrawerStore.getState().setOpen(false)
    }
    set({ open })
  },
}))

/**
 * Payer-guidelines sidebar — sits *inside* the right pane (not as a full-
 * height overlay), so it intentionally does NOT contend with the
 * encounter-list / notes / flags drawer slot. Coders open it alongside the
 * Final Code Review tab to keep client-specific payer rules visible while
 * they work the chart. Width is user-resizable and persisted across
 * reloads — a heavier user typically wants the same width on every chart.
 */
interface PayerGuidelinesDrawerState extends DrawerState {
  width: number
  setWidth: (width: number) => void
}

const PG_DRAWER_MIN_WIDTH = 260
const PG_DRAWER_MAX_WIDTH = 720
const PG_DRAWER_DEFAULT_WIDTH = 340

export const usePayerGuidelinesDrawerStore = create<PayerGuidelinesDrawerState>()(
  persist(
    (set, get) => ({
      open: false,
      width: PG_DRAWER_DEFAULT_WIDTH,
      toggle: () => set({ open: !get().open }),
      setOpen: (open) => set({ open }),
      setWidth: (width) =>
        set({ width: Math.min(PG_DRAWER_MAX_WIDTH, Math.max(PG_DRAWER_MIN_WIDTH, Math.round(width))) }),
    }),
    {
      name: 'workspace-payer-guidelines-drawer',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ width: state.width }),
    },
  ),
)

export const PG_DRAWER_BOUNDS = {
  min: PG_DRAWER_MIN_WIDTH,
  max: PG_DRAWER_MAX_WIDTH,
  default: PG_DRAWER_DEFAULT_WIDTH,
}
